import './CardSelectable.scss';

import React, { useCallback, useEffect, useState } from 'react';
import withCx, { CxProps } from 'fe-core/util/withCx';
import Box from 'fe-design-base/atoms/Box';
import { Props as BoxProps } from 'fe-design-base/atoms/Box/interface';
import Checkbox from 'fe-design-base/atoms/Checkbox';
import RadioButton from 'fe-design-base/atoms/RadioButton';
import Text from 'fe-design-base/atoms/Text';
import AlertText, {
  Variant as AlertTextVariant,
} from 'fe-design-base/molecules/AlertText';
import CardContainer from 'fe-design-base/molecules/CardContainer';

export const SELECTABLE_CARD_VARIANTS = ['radio', 'checkbox', 'plain'] as const;
export type Variant = (typeof SELECTABLE_CARD_VARIANTS)[number];
type CallbackParamType = { value: string | number; selected: boolean };

export interface CardSelectableProps
  extends Omit<BoxProps, 'onClick' | 'children'> {
  alertText?: string;
  alertVariant?: AlertTextVariant;
  content?: React.ReactNode;
  disabled?: boolean;
  illustration?: React.ReactNode;
  leftContent?: React.ReactNode;
  onClick?: (event: CallbackParamType) => void;
  selected?: boolean;
  subtitle?: string;
  title: string;
  value: string | number;
  variant?: Variant;
  vertical?: boolean;
  truncateTitle?: boolean;
  radioCanDeselect?: boolean;
  isAI?: boolean;
  isAIAnimating?: boolean;
}

const CardSelectable = ({
  alertText,
  alertVariant = 'error',
  cx,
  cxEl,
  content,
  disabled,
  illustration,
  leftContent,
  minw = 180,
  marginRight = 8,
  onClick,
  selected = false,
  subtitle,
  title,
  value,
  variant = 'radio',
  vertical,
  w,
  truncateTitle,
  radioCanDeselect,
  isAI,
  isAIAnimating,
  ...cardContainerProps
}: CardSelectableProps & CxProps) => {
  const [cardSelected, setCardSelected] = useState(selected);

  const showRadio = variant === 'radio';
  const showCheckbox = variant === 'checkbox';

  const showIllustration = illustration && vertical;
  const showLeftContent = leftContent && !vertical;

  useEffect(() => {
    setCardSelected(selected);
  }, [selected]);

  const handleClick = useCallback(() => {
    let newCardSelected;

    if (variant === 'radio') {
      newCardSelected = radioCanDeselect ? !cardSelected : true;
    } else {
      newCardSelected = !cardSelected;
    }

    setCardSelected(newCardSelected);

    onClick?.({
      value,
      selected: newCardSelected,
    });
  }, [cardSelected, onClick, radioCanDeselect, value, variant]);

  const handleKeydown = useCallback(
    // TODO: DB https://joinhomebase.atlassian.net/browse/FE-2199
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    key => {
      if (key.code === 'Enter' || key.code === 'Space') handleClick();
    },
    [handleClick]
  );

  // handling required but unused onChange for the RadioButton
  const handleChange = useCallback(() => null, []);

  return (
    <CardContainer
      w={w || 'fit-content'}
      selected={cardSelected}
      onClick={handleClick}
      onKeyDown={handleKeydown}
      disabled={disabled}
      pointer
      isAI={isAI}
      isAIAnimating={isAIAnimating}
      {...cardContainerProps}
    >
      <Box className={cx({ vertical })} opacity={disabled ? 0.3 : ''}>
        {showIllustration && (
          <Box mb={16} hcenter>
            {illustration}
          </Box>
        )}
        {showLeftContent && (
          <Box vcenter mr={16}>
            {leftContent}
          </Box>
        )}
        <Box
          minw={minw}
          mr={vertical ? '' : marginRight}
          grow={1}
          className={cxEl('text-container')}
          ellipsis={truncateTitle}
        >
          <Text noWrap={truncateTitle} variant="bodyBoldTight" color="mono900">
            {title}
          </Text>
          {subtitle && (
            <Box mt={vertical ? 8 : 4}>
              <Text variant="bodySmTight">{subtitle}</Text>
            </Box>
          )}
          {alertText && (
            <Box
              className={cxEl('alert-container')}
              mt={vertical ? 8 : 4}
              gap={8}
              flex
              vcenter
            >
              <AlertText variant={alertVariant} message={alertText} />
            </Box>
          )}
          {content && content}
        </Box>
        {showRadio && (
          <Box className={cxEl('radio-container')}>
            <RadioButton
              name={title}
              checked={cardSelected}
              disabled={disabled}
              onChange={handleChange}
              tabIndex={disabled ? -1 : 0}
            />
          </Box>
        )}
        {showCheckbox && (
          <Box className={cxEl('checkbox-container')}>
            <Checkbox
              name={title}
              aria-label={title}
              checked={cardSelected}
              disabled={disabled}
              tabIndex={disabled ? -1 : 0}
            />
          </Box>
        )}
      </Box>
    </CardContainer>
  );
};

export default withCx<CardSelectableProps>('FDBCardSelectable')(CardSelectable);
